import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api/axios';

const isLocalDev = () => {
    if (typeof window === 'undefined') {
        return false;
    }
    return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
};

const isRenderBaseUrl = () => String(api?.defaults?.baseURL || '').includes('onrender.com');

const shouldTryLocalStripeFallback = (error) => {
    return isLocalDev() && isRenderBaseUrl() && Number(error?.response?.status) >= 500;
};

const localStripeGet = async (path) => {
    return api.get(`http://localhost:5001${path}`, {
        timeout: 10000,
        withCredentials: true,
    });
};

export const fetchAllCreditProducts = createAsyncThunk("/fetchAllCreditProducts", async (_, thunkAPI) => {
    try {
        const response = await api.get("/api/stripe/creditproducts");

        return response.data;
    } catch (error) {
        if (shouldTryLocalStripeFallback(error)) {
            try {
                const fallbackResponse = await localStripeGet("/api/stripe/creditproducts");
                return fallbackResponse.data;
            } catch (fallbackError) {
                return thunkAPI.rejectWithValue(fallbackError.message || 'Failed to fetch credit products');
            }
        }
        return thunkAPI.rejectWithValue(error.message || 'Failed to fetch credit products');
    }
})

export const fetchSubscriptionProducts = createAsyncThunk("/fetchSubscriptionProducts", async (_, thunkAPI) => {
    try {
        // Check if we already have subscription products in the state
        const state = thunkAPI.getState();
        if (state.payments && state.payments.subscriptionProducts && state.payments.subscriptionProducts.length > 0) {
            // If we already have data and it's not stale, return it
            const lastFetchTime = state.payments.lastFetchTime;
            const cacheTime = 10 * 60 * 1000; // 10 minutes cache
            
            if (lastFetchTime && Date.now() - lastFetchTime < cacheTime) {
                console.log("Using cached subscription products");
                return state.payments.subscriptionProducts;
            }
        }
        
        // Otherwise make the API call
        console.log("Fetching subscription products from API");
        const response = await api.get("/api/stripe/subscriptions", {
            // Add a timeout to prevent hanging requests
            timeout: 10000 // 10 seconds
        });

        return response.data;
    } catch (error) {
        if (shouldTryLocalStripeFallback(error)) {
            try {
                const fallbackResponse = await localStripeGet("/api/stripe/subscriptions");
                return fallbackResponse.data;
            } catch (fallbackError) {
                return thunkAPI.rejectWithValue(fallbackError.message || 'Failed to fetch subscription products');
            }
        }
        return thunkAPI.rejectWithValue(error.message || 'Failed to fetch subscription products');
    }
})

export const checkoutSubsciptions = createAsyncThunk("/checkoutSubsciptions", async ({ priceId, billingCycle }, thunkAPI) => {
    try {
        const token = localStorage.getItem("token");

        const response = await api.post("/api/stripe/create-sub-session",
            { priceId, billingCycle },
            {
                headers: {
                    "auth-token": token
                },
                timeout: 10000 // 10 second timeout to prevent hanging requests
            }
        )

        return response.data;
    } catch (error) {
        console.error("Checkout subscription error:", error.response || error);
        
        // More detailed error information
        let errorMessage = 'Failed to create checkout session';
        
        if (error.response) {
            // The server responded with a status code outside of 2xx range
            if (error.response.status === 500) {
                errorMessage = 'Server error (500): The subscription service is temporarily unavailable';
            } else {
                errorMessage = `Server error (${error.response.status}): ${error.response.data?.message || 'Unknown error'}`;
            }
        } else if (error.request) {
            // Request was made but no response received
            errorMessage = 'No response received from server. Please check your network connection.';
        }
        
        return thunkAPI.rejectWithValue(errorMessage);
    }
})

export const fetchInvoices = createAsyncThunk("/fetchInvoices", async (_, thunkAPI) => {
    try {
        const token = localStorage.getItem("token");
        const response = await api.get("/api/stripe/invoices", {
            headers: { "auth-token": token },
            timeout: 10000,
        });
        return response.data;
    } catch (error) {
        return thunkAPI.rejectWithValue(error.message || 'Failed to fetch invoices');
    }
});

export const cancelSubscription = createAsyncThunk("/cancelSubsciption", async (_, thunkAPI) => {
    try {
        const token = localStorage.getItem("token");

        const response = await api.post(
            "/api/stripe/cancelsubscription",
            {},
            {
                headers: {
                    "auth-token": token
                }
            }
        )

        return response.data;
    } catch (error) {
        return thunkAPI.rejectWithValue(error.message || 'Failed to create cancel subcription');
    }
})

export const createCreditCheckoutSession = createAsyncThunk("/createCreditCheckoutSession", async ({ priceId }, thunkAPI) => {
    try {
        const token = localStorage.getItem("token");

        const response = await api.post(
            "/api/stripe/create-checkout-session",
            { priceId },
            {
                headers: {
                    "auth-token": token
                },
                timeout: 15000
            }
        );

        return response.data;
    } catch (error) {
        console.error("Credit checkout error:", error.response || error);

        let errorMessage = 'Failed to create credit checkout session';

        if (error.response) {
            errorMessage = `Server error (${error.response.status}): ${error.response.data?.error || error.response.data?.message || 'Unknown error'}`;
        } else if (error.request) {
            errorMessage = 'No response received from server. Please check your network connection.';
        }

        return thunkAPI.rejectWithValue(errorMessage);
    }
});

export const fetchCreditHistory = createAsyncThunk("/fetchCreditHistory", async ({ limit = 50, cursor = null } = {}, thunkAPI) => {
    try {
        const token = localStorage.getItem("token");
        const params = new URLSearchParams({ limit });
        if (cursor) params.set('cursor', cursor);
        const response = await api.get(`/api/credits/history?${params}`, {
            headers: { "auth-token": token },
            timeout: 10000,
        });
        return response.data;
    } catch (error) {
        return thunkAPI.rejectWithValue(error.message || 'Failed to fetch credit history');
    }
});

const paymentSlice = createSlice({
    name: "payments",
    initialState: {
        creditProducts: [],
        creditProductsLoading: false,
        creditProductsError: null,
        subscriptionProducts: [],
        subLoading: false,
        subError: null,
        checkoutLoading: false,
        checkoutError: null,
        cancelLoading: false,
        cancelError: null,
        invoices: [],
        invoicesLoading: false,
        invoicesError: null,
        lastFetchTime: null,
        creditHistory: [],
        creditHistoryLoading: false,
        creditHistoryError: null,
        creditHistoryNextCursor: null,
        creditHistoryHasMore: false,
    },
    reducers: {},
    extraReducers: (builder) => {
        builder
            // Fetching credit products
            .addCase(fetchAllCreditProducts.pending, (state) => {
                state.creditProductsLoading = true;
                state.creditProductsError = null;
            })
            .addCase(fetchAllCreditProducts.fulfilled, (state, action) => {
                state.creditProducts = action.payload;
                state.creditProductsLoading = false;
            })
            .addCase(fetchAllCreditProducts.rejected, (state, action) => {
                state.creditProductsLoading = false;
                state.creditProductsError = action.payload;
            })

            // Fetching subscription products
            .addCase(fetchSubscriptionProducts.pending, (state) => {
                state.subLoading = true;
                state.subError = null;
            })
            .addCase(fetchSubscriptionProducts.fulfilled, (state, action) => {
                state.subscriptionProducts = action.payload;
                state.subLoading = false;
                state.lastFetchTime = Date.now();
            })
            .addCase(fetchSubscriptionProducts.rejected, (state, action) => {
                state.subLoading = false;
                state.subError = action.payload;
            })

            // Checkout for subscriptions
            .addCase(checkoutSubsciptions.pending, (state) => {
                state.checkoutLoading = true;
                state.checkoutError = null;
            })
            .addCase(checkoutSubsciptions.fulfilled, (state, action) => {
                state.checkoutLoading = false;
            })
            .addCase(checkoutSubsciptions.rejected, (state, action) => {
                state.checkoutLoading = false;
                state.checkoutError = action.payload;
            })

            // Cancel subscription
            .addCase(cancelSubscription.pending, (state) => {
                state.cancelLoading = true;
                state.cancelError = null;
            })
            .addCase(cancelSubscription.fulfilled, (state, action) => {
                state.cancelLoading = false;
            })
            .addCase(cancelSubscription.rejected, (state, action) => {
                state.cancelLoading = false;
                state.cancelError = action.payload;
            })

            // Fetch invoices
            .addCase(fetchInvoices.pending, (state) => {
                state.invoicesLoading = true;
                state.invoicesError = null;
            })
            .addCase(fetchInvoices.fulfilled, (state, action) => {
                state.invoices = action.payload;
                state.invoicesLoading = false;
            })
            .addCase(fetchInvoices.rejected, (state, action) => {
                state.invoicesLoading = false;
                state.invoicesError = action.payload;
            })

            // Credit checkout
            .addCase(createCreditCheckoutSession.pending, (state) => {
                state.checkoutLoading = true;
                state.checkoutError = null;
            })
            .addCase(createCreditCheckoutSession.fulfilled, (state, action) => {
                state.checkoutLoading = false;
            })
            .addCase(createCreditCheckoutSession.rejected, (state, action) => {
                state.checkoutLoading = false;
                state.checkoutError = action.payload;
            })

            // Credit history
            .addCase(fetchCreditHistory.pending, (state) => {
                state.creditHistoryLoading = true;
                state.creditHistoryError = null;
            })
            .addCase(fetchCreditHistory.fulfilled, (state, action) => {
                state.creditHistoryLoading = false;
                state.creditHistory = action.payload.items || [];
                state.creditHistoryNextCursor = action.payload.nextCursor || null;
                state.creditHistoryHasMore = action.payload.hasMore || false;
            })
            .addCase(fetchCreditHistory.rejected, (state, action) => {
                state.creditHistoryLoading = false;
                state.creditHistoryError = action.payload;
            })
    }
})

export default paymentSlice.reducer;